import { jwtDecode, JwtPayload } from 'jwt-decode'
import * as Sentry from '@sentry/react'
import { AxiosInstance } from 'axios'
import Cookies from 'js-cookie'
import { info, logException } from './sentryLogger'
import { ALL_SLUGS } from '../parameters/clients'
import { getNextScreen } from '../context/fsmUtils'
import { DEFAULT_AUTH_STATE, FSM_STATES } from '../context/defaultState'
import { restoreSession } from '../context/session/converter'
import { loadLocalSession } from '../context/session/helpers'
import { CommonValueType } from '../context/types'
import { langIndexFromLocal } from '../i18n/useTranslation'
import { apiService, clearTokenRenewal, startTokenRenewal } from './apiServices'
import { FullParameters, PartialParameters } from '../parameters/types'
import { convertServerParams } from '../parameters/converter'
import { defaultParamsFitme } from '../parameters/defaultParamsFitme'
import _ from 'lodash'
import { QueryParams } from './types'
import { updateWholeTheme } from '../utils/theme'

export const COOKIE_TOKEN = 'access_token';
export const COOKIE_EMAIL = 'username';

export function decodeJwt(token: string): JwtPayload | undefined {
    try {
        // TODO contain email
        const decoded = jwtDecode(token)
        info('decoded', decoded)
        return decoded // Access the 'sub' claim directly
    } catch (error: any) {
        logException('decodeJwt', 'Error decoding JWT '+ token, error)
        return undefined // Return undefined or handle error appropriately
    }
}

// 30 days by default (autologin)
export const jwtExp2Date = (exp: number) => new Date(exp * 1000);
export const jwtExp2Days = (exp: number) => Math.floor((exp - (Date.now() / 1000)) / 86400);

let interceptorID: number | null = null
export function installLoginData(authApi: AxiosInstance, token: string, email?: string) {
    removeLoginData(authApi);
    interceptorID = authApi.interceptors.request.use(
        (config) => {
            config.headers.Authorization = `Bearer ${token}`
            return config
        },
        (error) => {
            return Promise.reject(error)
        }
    )
    const decoded = decodeJwt(token) as Required<JwtPayload>;
    const expires = jwtExp2Date(decoded.exp);
    email && Cookies.set(COOKIE_EMAIL, email, { expires, secure: true })
    Cookies.set(COOKIE_TOKEN, token, { expires, secure: true })

    Sentry.getCurrentScope()?.setUser({ id: decoded.sub })
    startTokenRenewal();
    
    info('Cookies & Bearer installed()', Cookies.get(COOKIE_TOKEN), Cookies.get(COOKIE_EMAIL))
    return token;
}

export const removeLoginData = (authApi: AxiosInstance) => {
    if (interceptorID) {
        authApi.interceptors.request.eject(interceptorID)
    }
    Cookies.remove(COOKIE_TOKEN)
    Cookies.remove(COOKIE_EMAIL)
    info('test deleted cookies', Cookies.get(COOKIE_TOKEN), Cookies.get(COOKIE_EMAIL));
    Sentry.getCurrentScope()?.setUser({ id: 'null' })
    clearTokenRenewal();
    info('Cookies & Bearer removed()')
    return '';
}

/**
 * 
 * @param token 
 * @param email 
 * @param slug 
 * @param source 
 * @param langIndex 
 * @param commons 
 */
export const postLogin = async (
    queryParams: Required<QueryParams>,
    // 
    commons: CommonValueType,
    serverParams?: PartialParameters // undefined if auto-login
) => {
    // TODO error if no queryParams: email, slug, source, from, language => optional ones ? Types ?
    const { token, email, slug, source, from, language } = queryParams
    info('postLogin', slug, source, from, email, language)
    const langIndex = language
    ? langIndexFromLocal(language)
    : commons.state.params.assessmentParams.langIndex;
    console.debug('postLogin', { langIndex, language})
    const initialLoginState: typeof DEFAULT_AUTH_STATE = {
        userId: email, // overriden by api call
        userEmail: email,
        token,
        slug: slug as ALL_SLUGS,
        source,
        from,
    }
    const loginState = await apiService.fetchLoginState(initialLoginState); // can throw
    info('loginState', { loginState })

    if(!serverParams || loginState.slug !== slug || loginState.source !== source) {
        serverParams && info('Saved slug & source reloaded ; must refetch parameters')
        const params = await apiService.fetchParameters(loginState.slug, loginState.source);
        serverParams = convertServerParams(
            params
        ) as unknown as PartialParameters; //
        updateWholeTheme(serverParams?.brandParams ?? defaultParamsFitme.brandParams);
    }

    const newParams: FullParameters = {
        brandParams: _.defaults(
            _.cloneDeep(serverParams?.brandParams), // will include the color
            commons.state.params.brandParams,
            defaultParamsFitme.brandParams,
        ),
        assessmentParams: _.defaults(
            _.cloneDeep(serverParams?.assessmentParams),
            commons.state.params.assessmentParams,
            defaultParamsFitme.assessmentParams,
        ),
    }
    newParams.assessmentParams['langIndex'] = langIndex;
    console.debug('postLogin', {newParams}, serverParams)
    const restoredSessionData = loadLocalSession()
    if (
        restoredSessionData?.restorable &&
        restoredSessionData.restorable.userId === loginState.userId &&
        restoredSessionData.restorable.slug === loginState.slug &&
        // restoredSessionData.restorable.source === loginState.source && TODO
        window.confirm(
            'A previous session has been found (' +
                new Date(
                    restoredSessionData.restorable.savedDateStr
                ).toLocaleString() +
                '). Do you want to restore it?'
        )
    ) {
        restoreSession(restoredSessionData, commons, loginState, newParams)
    } else {
        info('will resetFn', { newParams })
        commons.resetFn({
            fsmState: getNextScreen(FSM_STATES.AUTH, newParams),
            newParams,
            loginState,
        })
    }
}
